import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import FB from '../utilities/firebase';
import { useDispatch, useSelector } from 'react-redux';
import ApiProvider from './apiProvider';
import { useNotify } from 'hooks/useNotify';
import { FirebaseError } from 'firebase/app';
import api from 'api/api';
import {
  getUser,
  signOut as logOut,
  login,
} from 'pages/profile/redux-sagas/actions';
import { TLogin } from 'api';
import { TSignUpData } from 'pages';

export const AuthContext: any = createContext(null);

export type AuthContextType = {
  getToken: Function;
  signIn: Function;
  signUp: Function;
  googleSignIn: () => void;
  signOut: () => void;
  isLoading: boolean;
  validateEmail: Function;
  getUID: Function;
  isEmailVerified: Function;
  changePassword: Function;
  forgetPassword: Function;
};

const AuthProvider = ({ children }: any) => {
  const { openErrorNotify, openSuccessNotify } = useNotify();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { error, isLogged } = useSelector((sate: any) => sate.profile);

  const signOut = useCallback(async () => {
    await FB.signOut(FB.getAuth())
      .then((e) => {
        dispatch(logOut());
      })
      .catch(({ code, message }: any) => {
        console.log(code, message);
      });
  }, [dispatch]);

  useEffect(() => {
    if (error || !isLogged) {
      signOut();
    }
    if (isLogged) {
      FB.getAuth().onAuthStateChanged(async (user: any) => {
        !user && !isLogged && signOut();
      });
    }
  }, [error, isLogged, signOut]);

  const setDefaultTokenToHeaders = (token: string) => {
    api.defaults.headers = {
      ...api.defaults.headers,
      Authorization: `Bearer ${token}`,
    } as any;
  };

  const cacheErrorMapper = useCallback(
    (code: any) => {
      setIsLoading(false);
      if (code === 'auth/email-already-in-use')
        openErrorNotify('Correo electrónico ya registrado');
      if (code === 'auth/wrong-password' || code === 'auth/user-not-found')
        openErrorNotify('Correo electrónico o contraseña incorrecta');
      if (code === 'auth/too-many-requests')
        openErrorNotify(
          'Demasiados intentos fallidos, intente de nuevo más tarde',
        );
    },
    [openErrorNotify],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      return FB.signInWithEmailAndPassword(FB.getAuth(), email, password)
        .then(async ({ user }) => {
          setIsLoading(false);
          if (!isLogged && user) {
            setDefaultTokenToHeaders(await user.getIdToken());
            dispatch(getUser(email));
          }
        })
        .catch(({ code, message }) => cacheErrorMapper(code));
    },
    [cacheErrorMapper, dispatch, isLogged],
  );

  const validateEmail = useCallback(async () => {
    try {
      await FB.sendEmailVerification(FB.getAuth().currentUser!);
      openSuccessNotify('Correo de verificación enviado');
    } catch (error) {
      openErrorNotify(
        'Error al enviar correo de verificación, intente nuevamente más tarde',
      );
      console.log(error);
    }
  }, [openErrorNotify, openSuccessNotify]);

  const getUID = () => {
    return FB.getAuth().currentUser?.uid;
  };

  const isEmailVerified = useCallback(
    async (refresh: boolean): Promise<boolean | undefined> => {
      refresh && (await FB.getAuth().currentUser?.reload());
      const isVerified = FB.getAuth().currentUser?.emailVerified;
      return isVerified;
    },
    [],
  );

  const changePassword = useCallback(
    async (oldPassword: string, password: string) => {
      try {
        if (!FB.getAuth().currentUser) return false;
        const credential = FB.EmailAuthProvider.credential(
          FB.getAuth().currentUser?.email!,
          oldPassword,
        );
        await FB.reauthenticateWithCredential(
          FB.getAuth().currentUser!,
          credential,
        );
        await FB.updatePassword(FB.getAuth().currentUser!, password);
        openSuccessNotify('Contraseña actualizada');
        return true;
      } catch (error: FirebaseError | any) {
        openErrorNotify(
          error.code === 'auth/wrong-password'
            ? 'Contraseña antigua incorrecta'
            : error.message,
        );
        throw error;
      }
    },
    [openErrorNotify, openSuccessNotify],
  );

  const getToken = useCallback(async (force: boolean) => {
    return new Promise(async (resolve) => {
      const unsuscribe = FB.getAuth().onAuthStateChanged(async (user: any) => {
        if (user) {
          const token = await user.getIdToken(force);
          setDefaultTokenToHeaders(token);
          resolve(token);
          unsuscribe();
        }
      });
    });
  }, []);

  const forgetPassword = useCallback(
    async (email: string) => {
      try {
        await FB.sendPasswordResetEmail(FB.getAuth(), email, {
          url: window.location.href,
        });
        openSuccessNotify(`Correo enviado a ${email}`);
      } catch ({ code }: any) {
        cacheErrorMapper(code);
      }
    },
    [cacheErrorMapper, openSuccessNotify],
  );

  const googleSignIn = useCallback(async () => {
    await FB.signInWithPopup(FB.getAuth(), new FB.GoogleAuthProvider())
      .then(async ({ user }) => {
        setIsLoading(false);
        if (!isLogged && user) {
          setDefaultTokenToHeaders(await user.getIdToken());
          const fullName = user.displayName || '';

          const splitName = fullName.split(' ');
          const firstName = splitName[0];
          const lastName = splitName[1];

          const dataToLogin: TLogin = {
            email: user.email!,
            lastName,
            firstName,
          };

          dispatch(login(dataToLogin));
        }
      })
      .catch(({ code, message }) => cacheErrorMapper(code));
  }, [cacheErrorMapper, dispatch, isLogged]);

  const signUp = useCallback(
    async (data: TSignUpData) => {
      await FB.createUserWithEmailAndPassword(
        FB.getAuth(),
        data.email,
        data.password,
      )
        .then(async ({ user }) => {
          setIsLoading(false);
          if (!isLogged && user) {
            setDefaultTokenToHeaders(await user.getIdToken());
            const dataToLogin: TLogin = {
              email: data.email,
              lastName: data.lastName,
              firstName: data.firstName,
            };

            dispatch(login(dataToLogin));
          }
        })
        .catch(({ code, message }) => cacheErrorMapper(code));
    },
    [cacheErrorMapper, dispatch, isLogged],
  );

  const value: AuthContextType = useMemo(
    () => ({
      isLoading,
      getToken,
      signIn,
      signOut,
      signUp,
      validateEmail,
      getUID,
      isEmailVerified,
      changePassword,
      googleSignIn,
      forgetPassword,
    }),
    [
      changePassword,
      forgetPassword,
      getToken,
      googleSignIn,
      isEmailVerified,
      isLoading,
      signIn,
      signOut,
      signUp,
      validateEmail,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      <ApiProvider>{children}</ApiProvider>
    </AuthContext.Provider>
  );
};

export { AuthProvider };
